"use client";

import { Loader2, Pause, Play, Send, Trash2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";

interface VoiceNotePreviewProps {
  mode: "recording" | "preview";
  previewUrl?: string;
  durationMs: number;
  waveform: number[];
  isPaused?: boolean;
  onDiscard: () => void;
  onSend: () => void;
  onTogglePause?: () => void;
  isSending?: boolean;
}

function formatDuration(durationMs: number) {
  const totalSeconds = Math.max(0, Math.round(durationMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

const BAR_COUNT = 40;
const IDLE_BAR = 8;

/**
 * Fit a level series to exactly `BAR_COUNT` bars.
 *
 * A live recording is a rolling window, newest last — show its tail, padded on
 * the left, so the waveform scrolls in from the right like WhatsApp. A
 * finished take is the WHOLE note: resample it (peak per bucket, linear
 * stretch when shorter) so the start of the recording is not sliced away.
 */
export function normalizeBars(
  input: number[],
  mode: "live" | "take",
  targetCount = BAR_COUNT,
) {
  if (input.length === 0) {
    return mode === "live"
      ? Array.from({ length: targetCount }, () => IDLE_BAR)
      : Array.from(
          { length: targetCount },
          (_, index) => 18 + ((index * 17) % 42),
        );
  }
  if (mode === "live") {
    if (input.length >= targetCount) return input.slice(-targetCount);
    return [
      ...Array.from({ length: targetCount - input.length }, () => IDLE_BAR),
      ...input,
    ];
  }
  const bars: number[] = [];
  for (let index = 0; index < targetCount; index++) {
    if (input.length >= targetCount) {
      const start = Math.floor((index * input.length) / targetCount);
      const end = Math.max(
        start + 1,
        Math.floor(((index + 1) * input.length) / targetCount),
      );
      let peak = 0;
      for (let cursor = start; cursor < end; cursor++) {
        peak = Math.max(peak, input[cursor] ?? 0);
      }
      bars.push(peak);
      continue;
    }
    const position =
      targetCount === 1 ? 0 : (index * (input.length - 1)) / (targetCount - 1);
    const lower = Math.floor(position);
    const upper = Math.min(input.length - 1, lower + 1);
    const weight = position - lower;
    bars.push(
      (input[lower] ?? 0) * (1 - weight) + (input[upper] ?? 0) * weight,
    );
  }
  return bars;
}

/** WhatsApp-like voice-note surface for both active recording and pre-send review. */
export function VoiceNotePreview({
  mode,
  previewUrl,
  durationMs,
  waveform,
  isPaused = false,
  onDiscard,
  onSend,
  onTogglePause,
  isSending = false,
}: VoiceNotePreviewProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progressMs, setProgressMs] = useState(0);

  const isRecordingMode = mode === "recording";
  const bars = useMemo(
    () => normalizeBars(waveform, isRecordingMode ? "live" : "take"),
    [isRecordingMode, waveform],
  );
  const progressCount =
    durationMs > 0 ? Math.round((progressMs / durationMs) * bars.length) : 0;

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (mode !== "preview" || !previewUrl) return;
    setIsPlaying(false);
    setProgressMs(0);
    const handleTime = () => setProgressMs(audio.currentTime * 1000);
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => {
      setIsPlaying(false);
      setProgressMs(0);
    };
    audio.addEventListener("timeupdate", handleTime);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("ended", handleEnded);
    return () => {
      audio.removeEventListener("timeupdate", handleTime);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("ended", handleEnded);
    };
  }, [mode, previewUrl]);

  const togglePreviewPlayback = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) void audio.play().catch(() => setIsPlaying(false));
    else audio.pause();
  };

  const centerLabel = isRecordingMode
    ? isPaused
      ? "Resume recording"
      : "Pause recording"
    : isPlaying
      ? "Pause preview"
      : "Play preview";

  // ~104px tall: two compact rows instead of the old ~176px sheet, so going
  // from the 42px input row to this surface is a small step, not a lurch.
  return (
    <div
      className={cn(
        "rounded-[24px] bg-[#0b0c0f] px-3 py-2 text-white shadow-[0_-10px_30px_rgba(0,0,0,0.18)]",
        "dark:bg-[#08090b]",
      )}
      data-chat-voice-recorder
    >
      {previewUrl ? (
        <audio
          ref={audioRef}
          src={previewUrl}
          preload="metadata"
          className="hidden"
        >
          <track kind="captions" />
        </audio>
      ) : null}

      <div className="flex h-9 items-center gap-2.5">
        <span className="flex w-[3.75rem] shrink-0 items-center gap-1.5">
          {isRecordingMode ? (
            <span
              aria-hidden
              className={cn(
                "h-2 w-2 shrink-0 rounded-full bg-[#ff496a]",
                !isPaused && "animate-pulse motion-reduce:animate-none",
                isPaused && "bg-white/40",
              )}
            />
          ) : null}
          <span className="text-[17px] leading-none text-white tabular-nums">
            {formatDuration(isPlaying ? progressMs : durationMs)}
          </span>
        </span>
        <div
          className={cn(
            "flex h-8 min-w-0 flex-1 items-center gap-[3px] overflow-hidden",
            isRecordingMode ? "justify-end" : "justify-start",
          )}
        >
          {bars.map((bar, index) => (
            <span
              // biome-ignore lint/suspicious/noArrayIndexKey: waveform buckets are positional audio samples.
              key={index}
              className={cn(
                "w-[3px] shrink-0 rounded-full transition-colors",
                !isRecordingMode && index < progressCount
                  ? "bg-[#25d366]"
                  : "bg-white/70",
                isRecordingMode && isPaused && "bg-white/34",
              )}
              style={{
                height: `${Math.max(
                  4,
                  Math.min(30, Math.round((bar / 100) * 30)),
                )}px`,
              }}
            />
          ))}
        </div>
      </div>

      <div className="mt-1.5 flex h-11 items-center justify-between">
        <button
          type="button"
          onClick={onDiscard}
          disabled={isSending}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-white transition-colors hover:bg-white/8 disabled:opacity-50"
          aria-label="Discard voice note"
        >
          <Trash2 className="h-6 w-6 stroke-[2.2]" />
        </button>

        <button
          type="button"
          onClick={() => {
            if (isRecordingMode) {
              onTogglePause?.();
              return;
            }
            togglePreviewPlayback();
          }}
          disabled={isSending || (!isRecordingMode && !previewUrl)}
          className={cn(
            "flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-[2.5px] border-[#ff496a] text-[#ff496a] transition-transform active:scale-95 disabled:opacity-60",
            isPaused && "border-[#25d366] text-[#25d366]",
          )}
          aria-label={centerLabel}
        >
          {isRecordingMode ? (
            isPaused ? (
              <Play className="ml-0.5 h-5 w-5 fill-current" />
            ) : (
              <Pause className="h-5 w-5 fill-current" />
            )
          ) : isPlaying ? (
            <Pause className="h-5 w-5 fill-current" />
          ) : (
            <Play className="ml-0.5 h-5 w-5 fill-current" />
          )}
        </button>

        <button
          type="button"
          onClick={onSend}
          disabled={isSending}
          // Same focus rule as the composer's send button: never take focus.
          onPointerDown={(event) => event.preventDefault()}
          onMouseDown={(event) => event.preventDefault()}
          className={cn(
            "flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#25d366] text-black shadow-[0_8px_18px_rgba(37,211,102,0.25)] transition-transform hover:bg-[#22c55e] active:scale-95 disabled:opacity-70",
          )}
          aria-label="Send voice note"
        >
          {isSending ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <Send className="ml-0.5 h-5 w-5 fill-current stroke-[2.4]" />
          )}
        </button>
      </div>
    </div>
  );
}
